// deployment_class: customer_env_allowed
/**
 * Ask Kathedral Overlay — persistent right-side AI overlay. Plan 14 §Task 9
 * + Guidance System spec §5.6 (portal-aware AI).
 *
 * Behavior:
 *   - Renders on every authenticated page; hidden in unauthenticated
 *     marketplace browse (per Spec 12 §7.1) and on /auth/* routes.
 *   - Audio off by default (Design System spec §3.3 OFF state = coral);
 *     toggle to opt-in (ON state = green).
 *   - Reads the current portal context via getPortalFromPath. Switching
 *     portals breaks the AI session (per Guidance System §5.6).
 *   - Sends prompts to POST /v3/ai/ask with the portal context. The
 *     backend AI integration is V&D Part 9; Plan 14 ships the UI overlay
 *     and the request wrapper.
 *   - context_data overrides: each portal's routes can supply portal-
 *     specific context via the AskKathedralContext provider (extension
 *     point; not implemented in this task).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { getPortalFromPath } from '../utils/route-portal-mapping';
import type { PortalIdOrUnknown } from '../utils/route-portal-mapping';
import { askKathedral } from '../ai/useAskKathedral';
import { classifyAskFailure } from '../ai/ask-failure';
import { fetchOpenConversation, startNewConversation } from '../ai/useAskKathedral';

const HIDDEN_ROUTES = new Set(['/auth/login', '/auth/logout']);

/**
 * Collapsed-tab geometry, stated as an arithmetic relationship rather than two
 * unrelated Tailwind classes.
 *
 * The panel is `w-80` (320px) and slides right when closed. Whatever remains
 * on-screen — `PANEL_W_PX - COLLAPSED_OFFSET_PX` — is the tab the user grabs, and
 * it has to be wide enough for the label inside it (`▶ Ask` plus the header's
 * `px-3` padding, ~64px).
 *
 * It was not. The panel collapsed by `translate-x-72` (288px), leaving a 32px
 * peek around a ~64px label, so the tab rendered sliced down the middle at the
 * right viewport edge — visible as a stray bordered strip showing "▶ A". Two
 * numbers that had to agree were written 40 lines apart with nothing tying them
 * together, so nothing caught it. Found in the 2026-08-11 UI sweep (F2), fixed
 * once the screenshot tool could reproduce it deterministically.
 *
 * A test asserts the peek stays at least MIN_PEEK_PX wide.
 */
export const PANEL_W_PX = 320; // w-80
export const COLLAPSED_OFFSET_PX = 256; // translate-x-64
/** `▶ Ask` at text-body-s plus the header's px-3 padding on both sides. */
export const MIN_PEEK_PX = 64;

interface AskMessage {
  /**
   * `failure` is deliberately NOT a variant of `assistant`. A failed ask was
   * previously appended as an assistant turn, which made it indistinguishable
   * from something Kathedral actually said — while the server held no such turn
   * and HAD persisted the user's question. Client and server transcripts drifted
   * apart silently. Keeping failure a separate kind makes that unrepresentable.
   */
  role: 'user' | 'assistant' | 'failure';
  content: string;
}

export interface AskKathedralOverlayProps {
  /** Override portal context for tests. */
  forTestPortalContext?: PortalIdOrUnknown;
  /**
   * Override the API call for tests. Receives the portal + prompt; returns
   * the assistant's reply text. Default: POST /v3/ai/ask.
   */
  forTestAskFn?: (args: {
    portal: PortalIdOrUnknown;
    prompt: string;
    contextData: Record<string, unknown>;
  }) => Promise<string>;
}

export function AskKathedralOverlay({
  forTestPortalContext,
  forTestAskFn,
}: AskKathedralOverlayProps = {}): JSX.Element | null {
  const { status } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const sessionPortalRef = useRef<PortalIdOrUnknown | null>(null);

  const portal = forTestPortalContext ?? getPortalFromPath(location.pathname);

  // Per Guidance System §5.6: session breaks on portal switch. We track
  // the portal of the current session and reset messages whenever the
  // portal changes.
  useEffect(() => {
    if (sessionPortalRef.current && sessionPortalRef.current !== portal) {
      setMessages([]);
      setHistoryLoaded(false);
    }
    sessionPortalRef.current = portal;
  }, [portal]);

  // Load the thread the SERVER is already using, once, when the panel is opened.
  //
  // The model receives up to 20 prior turns on every ask. Rendering an empty panel
  // over that told the user they were starting fresh when they were not, and made
  // any reply referencing earlier context look invented. A failure to load history
  // is deliberately SILENT here: it degrades to the previous behaviour (an empty
  // thread) rather than blocking the user from asking, and the ask itself still
  // reports its own failures loudly.
  useEffect(() => {
    if (!open || historyLoaded) return;
    const portalContext = portalToAiContext(portal);
    if (portalContext === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOpenConversation({
          portalContext,
          partnershipId:
            portalContext === 'partnership' ? partnershipIdFromPath(location.pathname) : null,
        });
        if (cancelled) return;
        setMessages(
          res.messages.map((m) => ({ role: m.role, content: m.content }) as AskMessage),
        );
      } catch {
        /* history is additive — never block asking on it */
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, historyLoaded, portal, location.pathname, forTestPortalContext]);

  if (status !== 'authenticated') return null;
  if (HIDDEN_ROUTES.has(location.pathname)) return null;
  // The Executive cockpit (dashboard portal · the .kdash surface) has its OWN
  // integrated Ask-Kathedral panel — the global overlay must NOT render there or
  // it double-stacks + covers the cockpit content (UI_BUG_1e). On '/' this was
  // implicit (portal 'unknown'); the /dashboard route made it explicit.
  if (portal === 'dashboard') return null;

  async function handleNewConversation(): Promise<void> {
    const portalContext = portalToAiContext(portal);
    if (portalContext === null) return;
    try {
      await startNewConversation({
        portalContext,
        partnershipId:
          portalContext === 'partnership' ? partnershipIdFromPath(location.pathname) : null,
      });
      setMessages([]);
    } catch (err) {
      // A local-only clear would be the same lie in reverse: a blank panel over a
      // thread the model is still carrying. If the server did not end it, say so.
      setMessages((prev) => [
        ...prev,
        { role: 'failure', content: classifyAskFailure(err).userMessage },
      ]);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!input.trim()) return;
    const prompt = input.trim();
    setMessages((prev) => [...prev, { role: 'user', content: prompt }]);
    setInput('');
    setPending(true);
    try {
      const reply = forTestAskFn
        ? await forTestAskFn({ portal, prompt, contextData: {} })
        : await sendAsk({ portal, prompt, contextData: {}, pathname: location.pathname });
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      // Classified, never interpolated: the user gets an accurate cause and no
      // internal detail, and it is recorded as a failure rather than an answer.
      setMessages((prev) => [
        ...prev,
        { role: 'failure', content: classifyAskFailure(err).userMessage },
      ]);
    } finally {
      setPending(false);
    }
  }

  return (
    <aside
      aria-label="Ask Kathedral"
      className={
        // w-80 / translate-x-64 are PANEL_W_PX / COLLAPSED_OFFSET_PX above —
        // keep them in step or the collapsed tab clips its own label again.
        'fixed right-0 top-16 bottom-0 w-80 bg-paper border-l border-rule shadow-md flex flex-col ' +
        (open ? 'translate-x-0' : 'translate-x-64')
      }
      data-portal-context={portal}
    >
      {/* A panel heading, not a page landmark: a <header> here made the overlay a
          second "banner" beside the app's TopBar once Ask rendered on the home
          page (2026-10-20). One banner per page (WCAG 2.2 · landmark uniqueness). */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-rule">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? 'Collapse Ask Kathedral' : 'Expand Ask Kathedral'}
          className="text-body-s text-trust"
        >
          {open ? '◀ Ask' : '▶ Ask'}
        </button>
        {open && (
          // Ending a thread is a SERVER action, not a local clear — see
          // handleNewConversation. Shown only when the panel is open, because it
          // acts on a conversation the user can currently see.
          <button
            type="button"
            onClick={() => void handleNewConversation()}
            className="px-2 py-1 text-body-xs border border-rule bg-paper text-ink"
            aria-label="Start a new conversation"
          >
            New
          </button>
        )}
        <button
          type="button"
          role="switch"
          onClick={() => setAudioOn((a) => !a)}
          aria-checked={audioOn}
          aria-label="Voice narration"
          className={
            'px-2 py-1 text-body-xs border border-rule focus:outline-none focus:ring-2 focus:ring-trust ' +
            (audioOn ? 'bg-paper-green text-green-d' : 'bg-paper-soft text-coral-d')
          }
        >
          {/* Glyph differs per state so signal is not color-only. §9.2 */}
          <span aria-hidden="true">{audioOn ? '🔊' : '🔇'}</span>{' '}
          {audioOn ? 'Audio: ON' : 'Audio: OFF'}
        </button>
      </div>
      {open && (
        <>
          <p className="text-body-xs text-warm-gray-d px-3 pt-2">
            Portal: {portalLabel(portal)}
          </p>
          <div
            role="log"
            aria-live="polite"
            aria-busy={pending}
            className="flex-1 overflow-y-auto px-3 py-2 space-y-2"
          >
            {messages.length === 0 && (
              <p className="text-body-s text-warm-gray-d">
                Ask Kathedral about anything in this portal.
              </p>
            )}
            {messages.map((m, i) =>
              m.role === 'failure' ? (
                // role="alert" so it is ANNOUNCED rather than read as the next
                // answer in the log, and labelled in text so the distinction does
                // not rest on colour alone (Design System §9.2).
                <div
                  key={i}
                  role="alert"
                  className="p-2 text-body-s bg-paper-soft border border-coral text-coral-d"
                >
                  <span className="font-medium">Not answered — </span>
                  {m.content}
                </div>
              ) : (
                <div
                  key={i}
                  className={
                    'p-2 text-body-s ' +
                    (m.role === 'user'
                      ? 'bg-paper-soft text-ink'
                      : 'bg-paper-blue text-trust')
                  }
                >
                  {m.content}
                </div>
              ),
            )}
          </div>
          <form
            onSubmit={handleSubmit}
            className="px-3 py-2 border-t border-rule flex gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask…"
              className="flex-1 border border-rule px-2 py-1 text-body-s bg-paper"
              disabled={pending}
              aria-label="Ask Kathedral prompt"
            />
            <button
              type="submit"
              disabled={pending || !input.trim()}
              className="bg-trust text-paper px-3 py-1 text-body-s disabled:opacity-50"
            >
              Send
            </button>
          </form>
        </>
      )}
    </aside>
  );
}

// The overlay renders on every authenticated route except /auth/* and the
// Executive cockpit, which carries its own integrated Ask panel (unified into
// the floating, dockable Ask in Analyzer W8). Off-portal routes map to the
// agency-wide `dashboard` context. The /v3/ai/ask transport itself lives in the
// shared client (shell/ai/useAskKathedral).

/**
 * Map the frontend portal id to the backend AIPortalContext (data-analytics
 * carries a hyphen the backend spells with an underscore). Every route outside a
 * named portal — the agency home, projects, the board, account — asks in the
 * agency-wide `dashboard` context (E3 2026-10-20: Ask Kathedral is ALWAYS
 * available). Only the cockpit itself returns null; it has its own panel.
 */
export function portalToAiContext(portal: PortalIdOrUnknown): string | null {
  switch (portal) {
    case 'data-analytics':
      return 'data_analytics';
    case 'marketplace':
      return 'marketplace';
    case 'partnership':
      return 'partnership';
    case 'sales':
      return 'sales';
    case 'unknown':
      return 'dashboard';
    default:
      return null;
  }
}

/** Extract the partnership UUID from a /partnerships/:id path (null when absent). */
export function partnershipIdFromPath(pathname: string): string | null {
  const m = /^\/partnerships\/([^/?#]+)/.exec(pathname);
  return m ? m[1] : null;
}

async function sendAsk(args: {
  portal: PortalIdOrUnknown;
  prompt: string;
  contextData: Record<string, unknown>;
  pathname: string;
}): Promise<string> {
  const portalContext = portalToAiContext(args.portal);
  if (portalContext === null) {
    throw new Error('Ask Kathedral is not available on this screen.');
  }
  // Delegate to the shared, governed transport (one real path for every surface).
  return askKathedral({
    portalContext,
    prompt: args.prompt,
    contextBindings: args.contextData,
    partnershipId:
      portalContext === 'partnership' ? partnershipIdFromPath(args.pathname) : null,
  });
}

function portalLabel(portal: PortalIdOrUnknown): string {
  switch (portal) {
    case 'dashboard':
      return 'Executive Dashboard';
    case 'marketplace':
      return 'Marketplace';
    case 'partnership':
      return 'Partnership';
    case 'sales':
      return 'Sales';
    case 'data-analytics':
      return 'Data Analytics';
    case 'unknown':
      return 'Your agency';
  }
}
